import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, LayoutTemplate, Plus, RotateCcw, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  normalizeContent,
  type DocumentContent,
  type DocumentTypeDto,
  type TemplateDto,
  type TemplateFieldDef,
} from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Tabs,
  Textarea,
  useToast,
} from '@/components/ui';
import { DocumentEditor } from '@/editor/DocumentEditor';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/lib/session';

export function TemplatesPage() {
  const { can } = useSession();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TemplateDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<TemplateDto | null>(null);
  const [typesOpen, setTypesOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['templates'], queryFn: () => api.get<{ items: TemplateDto[] }>('/templates') });
  const { data: types } = useQuery({ queryKey: ['document-types'], queryFn: () => api.get<{ items: DocumentTypeDto[] }>('/document-types') });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['templates'] });
    queryClient.invalidateQueries({ queryKey: ['document-types'] });
  };

  const setDefault = useMutation({
    mutationFn: (templateId: string) => api.post(`/templates/${templateId}/default`),
    onSuccess: () => {
      invalidate();
      push('Default template updated', 'success');
    },
  });

  const duplicate = useMutation({
    mutationFn: (templateId: string) => api.post(`/templates/${templateId}/duplicate`),
    onSuccess: () => {
      invalidate();
      push('Template duplicated', 'success');
    },
  });

  const archive = useMutation({
    mutationFn: (templateId: string) => api.del(`/templates/${templateId}`),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
      push('Template archived', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not archive', 'danger'),
  });

  const restore = useMutation({
    mutationFn: () => api.post<{ restored: number }>('/templates/restore-builtins'),
    onSuccess: (result) => {
      invalidate();
      push(result.restored ? `${result.restored} built-in templates restored` : 'All built-in templates are already present', 'success');
    },
  });

  const grouped = (types?.items ?? []).map((type) => ({
    type,
    templates: (data?.items ?? []).filter((t) => t.documentTypeKey === type.key),
  }));

  return (
    <>
      <PageHeader
        title="Templates"
        description="The starting structure for each kind of document. Built-in templates are fully editable."
        actions={
          can('template:manage') ? (
            <>
              <Button icon={<RotateCcw className="size-4" />} loading={restore.isPending} onClick={() => restore.mutate()}>
                Restore built-ins
              </Button>
              <Button onClick={() => setTypesOpen(true)}>Document types</Button>
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
                New template
              </Button>
            </>
          ) : null
        }
      />

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <Card>
          <ErrorState error={error} onRetry={() => void refetch()} title="Could not load templates" />
        </Card>
      ) : (
        <div className="space-y-5">
          {grouped.map(({ type, templates }) => (
            <Card key={type.id}>
              <CardHeader
                title={
                  <span className="flex items-center gap-2">
                    {type.name}
                    <Badge tone="neutral">{type.referencePrefix}-001</Badge>
                  </span>
                }
                description={type.description ?? undefined}
              />
              {templates.length === 0 ? (
                <p className="px-5 py-6 text-[13px] text-ink-500">No templates for this type yet.</p>
              ) : (
                <ul className="divide-y divide-ink-100">
                  {templates.map((template) => (
                    <li key={template.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5">
                      <LayoutTemplate className="size-4 shrink-0 text-ink-300" />
                      <div className="min-w-0 flex-1 basis-48">
                        <p className="flex items-center gap-2 text-[13.5px] font-medium text-ink-800">
                          {template.name}
                          {template.isDefault ? <Badge tone="brand">default</Badge> : null}
                          {template.isSystem ? <Badge tone="neutral">built-in</Badge> : null}
                        </p>
                        <p className="mt-0.5 truncate text-[12px] text-ink-500">
                          {template.content.sections.length} sections · {template.fields.length} fields
                          {template.description ? ` · ${template.description}` : ''}
                        </p>
                      </div>
                      {can('template:manage') ? (
                        <>
                          {!template.isDefault ? (
                            <Button size="sm" icon={<Star className="size-3.5" />} onClick={() => setDefault.mutate(template.id)}>
                              Make default
                            </Button>
                          ) : null}
                          <Button size="sm" icon={<Copy className="size-3.5" />} onClick={() => duplicate.mutate(template.id)}>
                            Duplicate
                          </Button>
                          <Button size="sm" variant="primary" onClick={() => setEditing(template)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => setDeleting(template)} aria-label="Archive" />
                        </>
                      ) : (
                        <Button size="sm" onClick={() => setEditing(template)}>
                          View
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}

      {editing ? (
        <TemplateEditorModal
          template={editing === 'new' ? null : editing}
          types={types?.items ?? []}
          readOnly={!can('template:manage')}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
          }}
        />
      ) : null}

      <DocumentTypesModal open={typesOpen} onClose={() => setTypesOpen(false)} types={types?.items ?? []} onChanged={invalidate} />

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && archive.mutate(deleting.id)}
        title="Archive this template?"
        message="Existing documents keep their content. The template will no longer be offered for new documents."
        confirmLabel="Archive"
        loading={archive.isPending}
      />
    </>
  );
}

function TemplateEditorModal({
  template,
  types,
  readOnly,
  onClose,
  onSaved,
}: {
  template: TemplateDto | null;
  types: DocumentTypeDto[];
  readOnly: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push } = useToast();
  const [tab, setTab] = useState<'structure' | 'fields' | 'settings'>('structure');
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [documentTypeKey, setDocumentTypeKey] = useState(template?.documentTypeKey ?? types[0]?.key ?? '');
  const [isDefault, setIsDefault] = useState(template?.isDefault ?? false);
  const [fields, setFields] = useState<TemplateFieldDef[]>(template?.fields ?? []);
  const [content, setContent] = useState<DocumentContent>(
    normalizeContent(template?.content ?? { schemaVersion: 1, sections: [] }),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), description: description || undefined, documentTypeKey, fields, content, isDefault };
      return template ? api.put(`/templates/${template.id}`, payload) : api.post('/templates', payload);
    },
    onSuccess: () => {
      push(template ? 'Template updated' : 'Template created', 'success');
      onSaved();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save the template'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      width="full"
      title={template ? `Edit “${template.name}”` : 'New template'}
      description="Sections you add here become the starting structure for every document created from this template."
      footer={
        <>
          <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly ? (
            <Button variant="primary" loading={save.isPending} disabled={!name.trim() || !documentTypeKey} onClick={() => save.mutate()}>
              {template ? 'Save template' : 'Create template'}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {template?.isSystem && !readOnly ? (
          <Alert tone="info">
            This is a built-in template. Saving your changes makes it your organisation's own copy — you can always restore the originals later.
          </Alert>
        ) : null}

        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: 'structure' as const, label: 'Structure', count: content.sections.length },
            { id: 'fields' as const, label: 'Metadata fields', count: fields.length },
            { id: 'settings' as const, label: 'Settings' },
          ]}
        />

        {tab === 'structure' ? (
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <DocumentEditor content={content} onChange={setContent} readOnly={readOnly} />
          </div>
        ) : null}

        {tab === 'fields' ? (
          <FieldsEditor fields={fields} onChange={setFields} readOnly={readOnly} />
        ) : null}

        {tab === 'settings' ? (
          <div className="space-y-4">
            <Field label="Template name" required>
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} placeholder="Policy (house style)" />
            </Field>
            <Field label="Description" hint="Shown when someone picks a template.">
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} disabled={readOnly} />
            </Field>
            <Field label="Document type" required>
              <Select value={documentTypeKey} onChange={(e) => setDocumentTypeKey(e.target.value)} disabled={readOnly}>
                {types.map((type) => (
                  <option key={type.key} value={type.key}>
                    {type.name}
                  </option>
                ))}
              </Select>
            </Field>
            <label className="flex items-start gap-2.5">
              <input
                type="checkbox"
                checked={isDefault}
                disabled={readOnly}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="mt-0.5 size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-400"
              />
              <span className="text-[13px] text-ink-700">
                <span className="font-medium">Use as the default for this document type</span>
                <span className="mt-0.5 block text-ink-500">New documents of this type will start from this template.</span>
              </span>
            </label>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function FieldsEditor({ fields, onChange, readOnly }: { fields: TemplateFieldDef[]; onChange: (next: TemplateFieldDef[]) => void; readOnly: boolean }) {
  const update = (index: number, patch: Partial<TemplateFieldDef>) =>
    onChange(fields.map((field, i) => (i === index ? { ...field, ...patch } : field)));

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-ink-500">
        These appear in the document's Properties panel and are exported as YAML front matter.
      </p>

      {fields.length === 0 ? (
        <EmptyState title="No custom fields" description="Documents will still carry owner, classification, dates and tags." />
      ) : (
        <ul className="space-y-2">
          {fields.map((field, index) => (
            <li key={index} className="rounded-lg border border-ink-200 p-3">
              <div className="grid gap-2 sm:grid-cols-[1fr_1fr_140px_auto]">
                <Field label="Key">
                  <Input
                    value={field.key}
                    disabled={readOnly}
                    onChange={(e) => update(index, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    className="font-mono text-[12.5px]"
                  />
                </Field>
                <Field label="Label">
                  <Input value={field.label} disabled={readOnly} onChange={(e) => update(index, { label: e.target.value })} />
                </Field>
                <Field label="Type">
                  <Select value={field.type} disabled={readOnly} onChange={(e) => update(index, { type: e.target.value as TemplateFieldDef['type'] })}>
                    {['text', 'textarea', 'date', 'select', 'tags', 'user'].map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Select>
                </Field>
                {!readOnly ? (
                  <div className="flex items-end pb-1">
                    <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => onChange(fields.filter((_, i) => i !== index))} aria-label="Remove field" />
                  </div>
                ) : null}
              </div>
              {field.type === 'select' ? (
                <Field label="Options" hint="Comma separated." className="mt-2">
                  <Input
                    value={(field.options ?? []).join(', ')}
                    disabled={readOnly}
                    onChange={(e) => update(index, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                  />
                </Field>
              ) : null}
              <Field label="Help text" className="mt-2">
                <Input value={field.help ?? ''} disabled={readOnly} onChange={(e) => update(index, { help: e.target.value })} />
              </Field>
            </li>
          ))}
        </ul>
      )}

      {!readOnly ? (
        <Button
          icon={<Plus className="size-4" />}
          onClick={() => onChange([...fields, { key: `field_${fields.length + 1}`, label: 'New field', type: 'text' }])}
        >
          Add field
        </Button>
      ) : null}
    </div>
  );
}

function DocumentTypesModal({
  open,
  onClose,
  types,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  types: DocumentTypeDto[];
  onChanged: () => void;
}) {
  const { push } = useToast();
  const [form, setForm] = useState({ key: '', name: '', referencePrefix: '', description: '' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setForm({ key: '', name: '', referencePrefix: '', description: '' });
  }, [open]);

  const create = useMutation({
    mutationFn: () => api.post('/document-types', { ...form, description: form.description || undefined }),
    onSuccess: () => {
      onChanged();
      setForm({ key: '', name: '', referencePrefix: '', description: '' });
      push('Document type added', 'success');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not add the document type'),
  });

  return (
    <Modal open={open} onClose={onClose} title="Document types" description="Each type has its own reference prefix and templates." width="lg">
      <div className="space-y-4">
        <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
          {types.map((type) => (
            <li key={type.id} className="flex items-center gap-3 px-4 py-2.5">
              <Badge tone="neutral">{type.referencePrefix}</Badge>
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium text-ink-800">{type.name}</p>
                {type.description ? <p className="truncate text-[12px] text-ink-500">{type.description}</p> : null}
              </div>
              <span className="font-mono text-[11.5px] text-ink-400">{type.key}</span>
              {type.isSystem ? <Badge tone="neutral">built-in</Badge> : null}
            </li>
          ))}
        </ul>

        <div className="rounded-lg border border-ink-200 p-4">
          <p className="mb-3 text-[13px] font-semibold text-ink-800">Add a type</p>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Name" required>
              <Input
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setForm((current) => ({
                    ...current,
                    name,
                    key: current.key || name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
                    referencePrefix: current.referencePrefix || name.slice(0, 3).toUpperCase().replace(/[^A-Z]/g, ''),
                  }));
                }}
                placeholder="Work Instruction"
              />
            </Field>
            <Field label="Key" required hint="snake_case">
              <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} className="font-mono text-[12.5px]" />
            </Field>
            <Field label="Prefix" required hint="e.g. WIN">
              <Input
                value={form.referencePrefix}
                onChange={(e) => setForm({ ...form, referencePrefix: e.target.value.toUpperCase() })}
                maxLength={8}
                className="font-mono"
              />
            </Field>
          </div>
          <Field label="Description" className="mt-3">
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Button
            variant="primary"
            className="mt-3"
            loading={create.isPending}
            disabled={!form.name || !form.key || !form.referencePrefix}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            Add document type
          </Button>
        </div>
      </div>
    </Modal>
  );
}
